<script lang="ts" setup>
import { z } from 'zod'
import type { SectionWithIndex } from '~/types'

const toast = useToast()
const pb = useNuxtApp().$pb
interface Props extends Pick<SectionWithIndex, 'index' | 'id' | 'title' | 'image' | 'theme' | 'subtitle' | 'subdescription' | 'descriptionAdditional' | 'expand' | 'buttons' | 'description'> { }
const props = defineProps<Props>()

const email = ref('')
const emailIsValid = ref<boolean | undefined>(undefined)
const loading = ref(false)
const hasSaved = ref(false)
const { validateTurnstileToken, token } = useTurnstile()
async function sendEmail() {
  loading.value = true
  const emailSchema = z.string().email()
  const result = emailSchema.safeParse(email.value)
  if (result.success) {
    // Email is valid, proceed with sending
    try {
      await validateTurnstileToken(token.value)
      await pb.send(`/marketingpack`, { method: 'POST', body: { email: email.value } })
      emailIsValid.value = true
      hasSaved.value = true
    }
    catch (err) {
      console.log('Error sending email:', err)
      emailIsValid.value = false
    }
  }
  else {
    // Email is invalid, handle the error
    emailIsValid.value = false
    toast.add({
      title: 'Invalid email address',
      description: 'Please enter a valid email address.',
    })
  }
  loading.value = false
}

const buttonColours = computed(() => getButtonColor(props.theme))
</script>

<template>
  <div
    class="mx-auto max-w-screen-2xl w-full px-6 mt-24"
    :class="[theme]"
  >
    <HeadingText
      v-motion
      :visible="{ opacity: 1, rotate: 0, y: 0, x: 0, scale: 1 }"
      :initial="{ opacity: 0, rotate: 5, x: 20, scale: 0.5 }"
      :delay="350"
      tag="span"
      class="text-5xl  md:text-6xl lg:text-7xl text-center mb-2 heading inline-flex justify-center 2xl:text-9xl font-bold uppercase font-display w-full"
    >
      {{ title }}
    </HeadingText>
    <section
      v-motion
      :visible="{ opacity: 1 }"
      :initial="{ opacity: 0 }"
      :class="[theme]"
      class=" w-full rounded-4xl body relative overflow-clip"
    >
      <transition
        name="fade"
        mode="out-in"
      >
        <form
          v-if="!hasSaved"
          class="max-w-screen-md mx-auto md:px-10 lg:px-12 px-8 flex flex-col"
          @submit.prevent="sendEmail"
        >
          <div class="flex flex-col py-32">
            <p class="subtitle text-2xl md:text-3xl lg:text-4xl font-display font-black uppercase py-3 ">
              {{ props.subtitle }}
            </p>
            <div
              v-motion
              class="text w-full paragraph"
              :visible="{ opacity: 1, rotate: 0, y: 0, x: 0, scale: 1 }"
              :initial="{ opacity: 0, rotate: 5, y: 20, x: 30, scale: 0.5 }"
              :delay="400"
            >
              <section
                class="htmldescription"
                data-allow-mismatch
                v-html="props.subdescription"
              />
            </div>
            <div class="flex gap-3 items-center mt-6">
              <UFormGroup
                required
                :error="emailIsValid === false"
                class="w-full lg:flex-1"
              >
                <UInput
                  v-model="email"
                  placeholder="Enter your email"
                  size="xl"
                  class="w-full"
                  :ui="{ form: '!bg-white' }"
                  type="email"
                />
              </UFormGroup>

              <UButton
                size="xl"
                :disabled="loading"
                label="Submit"
                :color="buttonColours.button"
                type="submit"
                class="self-end ml-auto"
                :ui="{ shadow: 'shadow-xl', base: 'inline-flex items-center overflow-clip' }"
              />
            </div>
            <div class="mt-6">
              <NuxtTurnstile v-model="token" />
            </div>
            <div
              v-motion
              class="text mt-6 w-full paragraph"
              :visible="{ opacity: 1, rotate: 0, y: 0, x: 0, scale: 1 }"
              :initial="{ opacity: 0, rotate: 5, y: 20, x: 30, scale: 0.5 }"
              :delay="400"
            >
              <section
                class="htmldescription"
                data-allow-mismatch
                v-html="props.description"
              />
            </div>
          </div>
        </form>
        <div
          v-else
          class="max-w-screen-md mx-auto py-32 md:px-10 lg:px-12 px-8 flex flex-col"
        >
          <div
            v-motion
            class="text w-full subtitle text-2xl md:text-3xl lg:text-4xl font-display font-black uppercase py-3 "
            :visible="{ opacity: 1, rotate: 0, y: 0, x: 0, scale: 1 }"
            :initial="{ opacity: 0, rotate: 5, y: 20, x: 30, scale: 0.5 }"
            :delay="400"
          >
            <section
              class="htmldescription"
              data-allow-mismatch
              v-html="props.descriptionAdditional"
            />
          </div>
          <DownloadFull
            :id="id"
            :collection-id="SECTION_DEFAULT.collectionId"
            :src="props.image"
            :theme="theme"
          />
        </div>
      </transition>
    </section>
  </div>
</template>

<style scoped lang="css">
.paragraph :deep(p) {
  @apply pb-4;
}
.blue section.body {
  @apply bg-gradient-to-tl from-blue from-50% to-pink text-white;
}
.blue .heading {
  @apply text-blue;
}
.blue .subtitle {
  @apply text-yellow;
}
.blue .arrow {
  @apply text-pink;
}
.light-blue section.body {
  @apply bg-gradient-to-tl from-lightblue from-80% to-blue text-primary;
}
.light-blue .heading {
  @apply text-lightblue;
}
.light-blue .subtitle {
  @apply text-yellow;
}
.light-blue .arrow {
  @apply text-pink;
}
.pink section.body {
  @apply bg-gradient-to-tl from-pink from-60% via-pink via-60% to-lightblue text-white;
}
.pink .heading {
  @apply text-pink;
}
.pink .subtitle {
  @apply text-yellow;
}
.pink .arrow {
  @apply text-yellow;
}
</style>
